import React, { useState, useEffect } from 'react';
import { X, UserPlus, Mail, AlertCircle, Check, FolderPlus, Folder } from 'lucide-react';
import Dialog from './Dialog';

const NewProjectModal = ({ isOpen, onClose, userInfo, onProjectCreated }) => {
  const [projectName, setProjectName] = useState('');
  const [createDefaultCollection, setCreateDefaultCollection] = useState(true);
  const [email, setEmail] = useState('');
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setProjectName('');
      setCreateDefaultCollection(true);
      setEmail('');
      setCollaborators([]);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  const handleAddCollaborator = (e) => {
    e.preventDefault();
    
    if (!email.trim()) return;
    
    // Check if collaborator already exists
    const existingCollaborator = collaborators.find(collab => 
      collab.email.toLowerCase() === email.trim().toLowerCase()
    );
    
    if (existingCollaborator) {
      setError('This collaborator has already been added');
      return;
    }
    
    // Add collaborator to local list (we'll validate on server when creating project)
    const newCollaborator = {
      id: Date.now().toString(), // Temporary ID
      email: email.trim(),
      name: email.trim().split('@')[0], // Use email prefix as temporary name
      isTemp: true // Mark as temporary until validated by server
    };
    
    setCollaborators([...collaborators, newCollaborator]);
    setEmail('');
    setError(null);
  };

  const handleRemoveCollaborator = (id) => {
    setCollaborators(collaborators.filter(collab => collab.id !== id));
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!projectName.trim()) {
      setError('Please enter a project name');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const collaboratorEmails = collaborators.map(collab => collab.email);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: projectName.trim(),
          userId: userInfo.userId,
          collaborators: collaboratorEmails,
          createDefaultCollection
        })
      });
      
      const responseText = await response.text();
      console.log('Response status:', response.status);
      console.log('Response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        console.error('Raw response text:', responseText);
        throw new Error(`Server returned invalid JSON response: ${responseText.substring(0, 200)}...`);
      }
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create project');
      }
      
      setSuccess(true);
      
      // Notify parent component
      if (onProjectCreated) {
        onProjectCreated(data.project);
      }
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6 max-w-lg w-full bg-slate-800">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <FolderPlus className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-slate-100">Create New Project</h3>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 p-1 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleCreateProject} className="space-y-6">
          {/* Project Name */}
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-slate-300 mb-2">
              Project Name *
            </label>
            <input
              type="text"
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-slate-100 placeholder-slate-500"
              placeholder="Enter project name"
              required
            />
          </div>
          
          {/* Default Collection Option */}
          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <input
              type="checkbox"
              id="createDefaultCollection"
              checked={createDefaultCollection}
              onChange={(e) => setCreateDefaultCollection(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="createDefaultCollection" className="flex items-center gap-2 text-sm text-slate-300">
              <Folder className="h-4 w-4 text-blue-400" />
              Create a default collection
            </label>
          </div>
          
          {/* Add Collaborators */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Collaborators (Optional)
            </label>
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-slate-100 placeholder-slate-500"
                />
              </div>
              <button
                type="button"
                onClick={handleAddCollaborator}
                disabled={!email.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
              >
                <UserPlus size={16} />
                Add
              </button>
            </div>
            
            {/* Collaborators List */}
            {collaborators.length > 0 && (
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {collaborators.map(collaborator => (
                  <div key={collaborator.id} className="flex justify-between items-center p-2 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center text-white text-xs">
                        {collaborator.name[0].toUpperCase()}
                      </div>
                      <span className="text-sm text-slate-300">{collaborator.email}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveCollaborator(collaborator.id)}
                      className="text-slate-400 hover:text-red-400 p-1 rounded transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Error and Success Messages */}
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-700 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          {success && (
            <div className="p-3 bg-green-900/20 border border-green-700 rounded-lg flex items-center gap-2 text-green-400">
              <Check size={16} />
              <span className="text-sm">Project created successfully!</span>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !projectName.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                <>
                  <FolderPlus size={16} />
                  Create Project
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default NewProjectModal;